# XP87 Application Manifest
# Defines routes, layouts, and navigation structure
# Routes are domain-namespaced (/materials/, /config/, /audit/) for future extensibility

app:
  type: App
  # Browser-tab/shell title resolves from the single brand token (locale app.name).
  # The emitter resolves $t.* in the app title; rebranding is one locale-key change.
  name: $t.app.name
  # F53b §A — universal /today landing for every authenticated user. The per-role
  # default_routes map (F53a B-003: Operator→/home, Supervisor/Admin→/dashboard)
  # collapses to a single default: /today subsumes both the dashboard and the
  # operator /home tile view.
  default_route: /today

  # F912 / INC-BETA9-MOCK-AUTH-IN-PROD-01: production builds use real OIDC + PKCE
  # against Keycloak. The XP85 emitter switches on `provider:` at build time —
  # this manifest is what ships in v1.0.0-beta.12+. The previous `mock` value
  # leaked the in-memory mock provider into production, which the F5 fix closes.
  #
  # `authority:` is the PRODUCTION value. The emitted xp85-auth.js detects
  # localhost at runtime and substitutes `http://localhost:8080` for the host
  # part while preserving the realm path (`/auth/realms/acme`). The same emitted
  # bundle works for both prod (xp87.accurentis.com, nginx-proxied) and local-dev
  # (docker-compose.local.yml). Avoids two app.cir.yaml variants.
  #
  # F72 Phase A: realm slug is `acme` — the dev/test tenant realm imported by
  # `deploy/keycloak/realms/acme-realm.json`. SA browser login (system realm)
  # is API-only at present; SPA-side realm selection is M3 territory (F72b).
  #
  # client_id: matches the public xp87-spa client in deploy/keycloak/realms/acme-realm.json
  # audience: scopes the access token to the XP87 API, so /api/* endpoints accept it.
  auth:
    login_route: /login
    provider: oidc
    authority: https://xp87.accurentis.com/auth/realms/acme
    client_id: xp87-spa
    audience: xp87-api
    scope: openid profile email

  layouts:
    shell:
      children:
        - xp87-header
        - xp87-sidebar
        - page-slot
      spatial:
        display: grid
        areas: "'header' 'content'"
        columns: 1fr
        rows: $size.header-height 1fr
        height: 100dvh
        overflow: hidden
        responsive:
          lg:
            areas: "'header header' 'sidebar content'"
            # F63 spec v2: the sidebar track sizes to the sidebar's own
            # inline-size (~4rem collapsed, ~16rem expanded) so content grows
            # to fill the released space when the rail collapses.
            columns: auto 1fr
            rows: $size.header-height 1fr
    minimal:
      children:
        - page-slot

  routes:
    # F53b §A — /home + /dashboard removed (replaced by /today, declared below).
    # F79 — OEE dashboard removed; /today is the universal landing.

    # F53b §C — notifications panel (the F71 nav-notifications NavItem routes here)
    - path: /notifications
      page: page-notifications
      layout: shell
      auth: stub

    # F53b §A — universal /today landing (replaces the deleted /home + /dashboard; default_route + nav-today wired)
    - path: /today
      page: page-today
      layout: shell
      auth: stub

    # Tracking
    - path: /tracking
      page: page-tracking-dashboard
      layout: shell
      auth: stub

    - path: /tracking/records
      page: page-tracking-record-list
      layout: shell
      auth: stub

    - path: /tracking/reviews
      page: page-tracking-review-queue
      layout: shell
      auth: stub

    - path: /tracking/records/new
      page: page-tracking-record-create
      layout: shell
      auth: stub

    - path: /tracking/records/:recordId
      page: page-tracking-record-detail
      layout: shell
      auth: stub

    - path: /tracking/records/:recordId/amend
      page: page-tracking-record-amend
      layout: shell
      auth: stub

    - path: /tracking/records/:recordId/sign
      page: page-tracking-record-sign
      layout: shell
      auth: stub

    # Tracking Templates — list, detail, and YAML editor. The detail page owns
    # the lifecycle actions (Make Effective / Deprecate); its "Edit YAML" action
    # and the list's "Create" action route to /tracking/forms/:id/edit
    # (create-mode via /tracking/forms/new/edit) — F80 FR-7 consolidation.
    # F79 removed the per-entry /tracking/records/:recordId/audit route + page; the
    # audit trail is now reached via the entity-detail → /audit?entityType=… flagship.
    - path: /tracking/forms
      page: page-tracking-form-list
      layout: shell
      auth: stub
    - path: /tracking/forms/:id
      page: page-tracking-form-detail
      layout: shell
      auth: stub
    # F80 FR-7 — YAML editor consolidated under /tracking/forms
    # (create-mode via /tracking/forms/new/edit).
    - path: /tracking/forms/:id/edit
      page: page-tracking-form-editor
      layout: shell
      auth: stub

    # F80 — form assignments (template ↔ asset-class bindings)
    - path: /tracking/assignments
      page: page-tracking-assignment-list
      layout: shell
      auth: stub

    # F914 carve item 3 — vocab-create entries-list validation surface
    - path: /tracking/reasons/new
      page: page-tracking-vocab-create
      layout: shell
      auth: stub

    # User Profile
    - path: /profile
      page: page-user-profile
      layout: shell
      auth: stub

    # Feature #1630 — the tenant's page customizations. `feature:` here is the
    # ROUTE gate; the API route gate is the security boundary and answers 404
    # when the feature is off, which this mirrors so the SPA does not offer a
    # door that leads to one.
    - path: /customizations
      feature: ui_customization
      page: page-ui-customizations
      layout: shell
      auth: stub

    # Documentation (F500) — sidebar nav via nav-docs
    - path: /docs
      page: page-docs
      layout: shell
      auth: stub

    # Feature #1909 — the voice check page (FR-11). No nav entry: the chat widget's voice menu
    # and voice errors link here (FR-11.6).
    - path: /ai/voice-check
      page: page-ai-voice-check
      layout: shell
      auth: stub

    # Materials -- Lots
    - path: /materials/lots
      feature: materials
      page: page-lot-list
      layout: shell
      auth: stub

    - path: /materials/lots/receive
      feature: materials
      page: page-lot-receive
      layout: shell
      auth: stub

    - path: /materials/lots/:lotId
      feature: materials
      page: page-lot-detail
      layout: shell
      auth: stub

    - path: /materials/lots/:lotId/inspect
      feature: materials
      page: page-inspection
      layout: shell
      auth: stub

    - path: /materials/lots/:lotId/scrap
      feature: materials
      page: page-lot-scrap
      layout: shell
      auth: stub

    # Materials — Reference Data
    - path: /materials/classes
      feature: materials
      page: page-class-list
      layout: shell
      auth: stub

    # Literal /new must precede the /:id param route (router matches in
    # declaration order — same rule as /admin/roles/create and
    # /admin/reason-vocabularies/new) so it is not swallowed as id="new".
    - path: /materials/classes/new
      feature: materials
      page: page-class-create
      layout: shell
      auth: stub

    - path: /materials/classes/:id
      feature: materials
      page: page-class-detail
      layout: shell
      auth: stub

    - path: /materials/definitions
      feature: materials
      page: page-def-list
      layout: shell
      auth: stub

    # Literal /new precedes /:id (see note on /materials/classes/new).
    - path: /materials/definitions/new
      feature: materials
      page: page-def-create
      layout: shell
      auth: stub

    - path: /materials/definitions/:id
      feature: materials
      page: page-def-detail
      layout: shell
      auth: stub

    # Audit — the canonical entity-detail → audit deep-link is
    # /audit?entityType=…&entityId=… (screen-guide.md §6c forward channel),
    # not a path param; the /audit/:entityId route was dead (audit-trail.cir.yaml
    # never read $route.entityId into its filters) and its one caller
    # (lot-detail.cir.yaml) has been consolidated onto the query-param form.
    - path: /audit
      page: page-audit-trail
      layout: shell
      auth: stub
    - path: /audit/entry/:auditId
      page: page-audit-detail
      layout: shell
      auth: stub

    # Admin
    # The Platform admin routes (/admin/tenants*, /admin/system) carry the ROUTE permission gate:
    # a caller whose /api/me/permissions lacks System:admin — granted only to a verified system
    # admin — is sent to /errors/access-denied instead of mounting a page whose API refuses them.
    # The API's permission filter stays the security boundary; this mirrors it.
    - path: /admin/tenants
      permission: "System:admin"
      page: page-admin-tenant-list
      layout: shell
      auth: stub

    - path: /admin/tenants/create
      permission: "System:admin"
      page: page-admin-tenant-create
      layout: shell
      auth: stub

    - path: /admin/tenants/:tenantId
      permission: "System:admin"
      page: page-admin-tenant-detail
      layout: shell
      auth: stub

    - path: /admin/tenants/:tenantId/suspend
      permission: "System:admin"
      page: page-admin-tenant-suspend
      layout: shell
      auth: stub

    - path: /admin/tenants/:tenantId/activate
      permission: "System:admin"
      page: page-admin-tenant-activate
      layout: shell
      auth: stub

    - path: /admin/tenants/:tenantId/invitations
      permission: "System:admin"
      page: page-admin-tenant-invitations
      layout: shell
      auth: stub

    # F78c — platform-admin feature editor (PUT /api/platform/tenants/:tenantId/config).
    - path: /admin/tenants/:tenantId/features
      permission: "System:admin"
      page: page-admin-tenant-features
      layout: shell
      auth: stub

    - path: /admin/my-tenant/profile
      page: page-admin-my-tenant-profile
      layout: shell
      auth: stub

    # F78c — tenant-admin read-only feature view (GET /api/my-tenant/config).
    - path: /admin/my-tenant/features
      page: page-admin-my-tenant-features
      layout: shell
      auth: stub

    - path: /admin/users
      page: page-admin-user-list
      layout: shell
      auth: stub

    - path: /admin/users/:userId
      page: page-admin-user-detail
      layout: shell
      auth: stub

    - path: /admin/invitations
      page: page-admin-invitation-list
      layout: shell
      auth: stub

    - path: /admin/roles
      page: page-admin-role-list
      layout: shell
      auth: stub

    # Static /create must precede the /:roleName param route so it is not
    # swallowed as a role named "create".
    - path: /admin/roles/create
      page: page-admin-role-create
      layout: shell
      auth: stub

    - path: /admin/roles/:roleName/edit
      page: page-admin-role-edit
      layout: shell
      auth: stub

    - path: /admin/roles/:roleName
      page: page-admin-role-detail
      layout: shell
      auth: stub

    - path: /admin/system
      permission: "System:admin"
      page: page-admin-system-info
      layout: shell
      auth: stub

    # F53b §F — /admin/audit removed: audit lifted to its own /audit module.
    # Route + nav entry + admin page deleted per canonical-deliverables (no co-existing old/new).

    # F53b §E.5 — supervisor override list (tracking amendment audit)
    - path: /admin/supervisor-overrides
      page: page-supervisor-overrides
      layout: shell
      auth: stub

    # F53a G8 — reason vocabulary admin
    - path: /admin/reason-vocabularies
      page: page-reason-vocabulary-list
      layout: shell
      auth: stub

    # Alias to vocab-create page (literal `new` must precede `:id` rule)
    - path: /admin/reason-vocabularies/new
      page: page-tracking-vocab-create
      layout: shell
      auth: stub

    # /:id/edit must precede /:id (router matches in declaration order —
    # same rule as the literal /new above and /materials/classes/new) so
    # the edit route is not swallowed by the detail route.
    - path: /admin/reason-vocabularies/:id/edit
      page: page-reason-vocabulary-edit
      layout: shell
      auth: stub

    - path: /admin/reason-vocabularies/:id
      page: page-reason-vocabulary-detail
      layout: shell
      auth: stub

    # Feature #610 — the per-transaction reason view: the registry, one
    # transaction's resolved policy, and the codes editor (the vocabulary form
    # in transaction mode). /:id/codes precedes /:id, as /:id/edit does above.
    - path: /admin/reason-transactions
      page: page-reason-transaction-list
      layout: shell
      auth: stub

    - path: /admin/reason-transactions/:id/codes
      page: page-reason-transaction-codes
      layout: shell
      auth: stub

    - path: /admin/reason-transactions/:id
      page: page-reason-transaction-detail
      layout: shell
      auth: stub

    # Asset — top-level section (M1 UI surface reduction: asset class + instance
    # management is a first-class M1 surface, lifted out of /config/ into its own
    # /asset/ namespace and nav section). Gated on the `asset` feature.
    - path: /asset/classes
      feature: asset
      page: page-config-asset-class-list
      layout: shell
      auth: stub

    - path: /asset/classes/:id
      feature: asset
      page: page-config-asset-class-editor
      layout: shell
      auth: stub

    # F88 — Asset instance management (real Assets domain API). Literal /new
    # must precede the /:id param route (router matches in declaration order
    # — same rule as /materials/classes/new and /admin/roles/create).
    - path: /asset/instances
      feature: asset
      page: page-config-asset-instance-list
      layout: shell
      auth: stub

    - path: /asset/instances/new
      feature: asset
      page: page-config-asset-instance-create
      layout: shell
      auth: stub

    - path: /asset/instances/:id
      feature: asset
      page: page-config-asset-instance-editor
      layout: shell
      auth: stub

    # F914 carve items 5+6 — error landing pages. The xp87-error-router.js
    # helper redirects here on xp85-data-error events with 404/403 hints.
    - path: /errors/not-found
      page: page-error-not-found
      layout: shell
      auth: stub

    - path: /errors/access-denied
      page: page-error-access-denied
      layout: shell
      auth: stub

# --- Header ---

xp87-header:
  type: Section
  permission: "public"
  semantic_role: banner
  # F63 #2: drop the left padding so the hamburger sits flush to the edge
  # and its 4rem-wide button aligns with the 4rem rail below it. Right side
  # padding is applied on header-controls so the theme toggle doesn't touch
  # the edge.
  spatial:
    display: flex
    align: center
    justify: space-between
    min_height: $size.header-height
  tokens:
    background: $color_scheme.light.color.surface-elevated
    shadow: $color_scheme.light.shadow.sm
  # Product wordmark (header-brand-a, in header-left next to the hamburger).
  # The right slot carries the notification bell (same unread-count source as
  # the Notifications nav).
  children:
    - header-left
    - header-controls

header-left:
  type: Section
  spatial:
    display: flex
    direction: row
    align: center
    gap: $spacing.sm
  children:
    - header-hamburger
    - header-brand-a

# Product wordmark: "XP" in on-surface text + raised italic brand-orange "87".
# The BrandMark node renders the single canonical treatment shared with the
# XP85 auth overlay (.xp85hr-brand) via BrandCssGenerator, so the mark reads
# identically on every surface — including the translateY raise the plain token
# path cannot express. font_size sizes it for the 3.5rem header bar; the accent
# derives at 0.85×. The a11y label announces the whole product name.
header-brand-a:
  type: BrandMark
  content: XP87
  a11y:
    label: $t.app.name
  tokens:
    font_size: 1.5rem

# --- Header controls (right slot) ---

# Right-aligned header controls. Inline padding keeps the bell off the
# viewport edge (the left edge of the header is intentionally flush per the
# hamburger note on xp87-header above; this group is pushed right by the
# header's justify: space-between, so its inline padding reads as right gap).
header-controls:
  type: Section
  # Shell-layout slot, always rendered (like xp87-header) — "public" sentinel;
  # its only child (header-bell) self-gates on Notification:read.
  permission: "public"
  spatial:
    display: flex
    direction: row
    align: center
    gap: $spacing.sm
    padding_inline: $spacing.md
  children:
    - header-ai-dock
    - header-bell
    - header-onboarding-tour

# Global AI launcher — a persistent "Ask AI" button in the shell header, present
# on every page. Opens a right-side slide-over embedding <xp87-chat-widget> so
# the user can chat or dictate with the assistant (and be routed to editors)
# from anywhere, without returning to /today. Self-gates on AiSummary:read (the
# read-tier AI permission granted to every role; the chat endpoint enforces its
# own write-trust separately).
header-ai-dock:
  type: Widget
  ref: xp87-ai-dock
  permission: "AiSummary:read"
  params:
    label: $t.app.ai_dock_label
    api-url: /api/ai/chat

# Feature #1315 — first-run guided tour. Rides in the header controls purely as a mount
# point (the same slot the AI dock proves a fixed overlay escapes from cleanly);
# it renders nothing inline and paints a viewport-level overlay only while the
# tour runs. Shown to every authenticated user — what it points at is already
# filtered by each nav node's own permission/feature gate, and a step whose
# target is hidden is dropped, so the tour needs no gate of its own ("public"
# is the shell-slot sentinel header-controls already uses).
header-onboarding-tour:
  type: Widget
  ref: xp87-onboarding-tour
  permission: "public"

# Notification bell — same unread-count source as the Notifications nav item
# (badge: $notifications.unread_count). The emitter auto-wires the
# notifications context to this header subtree because it references
# $notifications.* in `badge` (IdentityBindingEmitter subtree scan), so the
# count stays live with the nav badge. Clicking navigates to the
# notifications review list. Uses the `bell` glyph (matches nav-notifications).
header-bell:
  type: NavItem
  permission: "Notification:read"
  route: /notifications
  icon: $icon.bell
  badge: $notifications.unread_count
  a11y:
    label: $t.nav.notifications_label
  active_match: prefix
  spatial:
    display: flex
    align: center
    justify: center
    width: 4rem
    min_height: $size.touch-target
  tokens:
    background: transparent
    color: $color_scheme.light.color.on-surface-muted
    radius: $radius.md
  states:
    hover:
      background: $color_scheme.light.color.surface-variant
      motion:
        transition: $motion.ease.out.150
        reduced: none

header-hamburger:
  type: Button
  content: $t.header.hamburger_icon
  a11y:
    label: $t.header.toggle_nav
  action:
    type: custom
    event: xp85-sidebar-toggle
  # F63 sidebar UX spec v2 D4: hamburger is the SOLE sidebar toggle at every
  # breakpoint. Drives desktop collapse/expand on lg+ and mobile slide-over
  # on sm/md — the xp85 emitter's connectedCallback dispatches the right
  # behavior from the same event based on viewport width.
  spatial:
    display: flex
    align: center
    justify: center
    width: 4rem
    min_height: $size.touch-target
  tokens:
    background: transparent
    font_size: $font.size.xl
    # Align with sidebar section icons (muted on-surface) rather than the
    # default ghost-info blue — #5 in the F63 polish pass.
    color: $color_scheme.light.color.on-surface-muted
  # The hover affordance the sole sidebar toggle owes, in the one key that
  # renders it: `states:` is what StateCssGenerator reads. Declared under
  # `tokens:` these never emitted — ParseTokens kept only scalar children and
  # dropped a nested mapping in silence — so the toggle has had no hover
  # highlight while its neighbour header-bell, twelve lines above, has carried
  # this same pair in `states:` and rendered it.
  #
  # There is deliberately no focus state here, and no `focus_visible` one either — that spelling
  # matches no arm of StateCssGenerator.GetSelector and is dropped in silence. FocusCssGenerator
  # emits the ring for any interactive node that declares no `focus` state, so declaring one here
  # would switch it off and could not replace it: EmitStateProperties writes six properties and
  # `outline` is not among them.
  #
  # MEASURED, and the reason this comment no longer promises a ring: the outline it emits is
  # `$border.focus`, which is `0.125rem solid $color_scheme.light.color.accent` (tokens.cir.yaml:156)
  # — a NESTED reference. TokenMap.Resolve is one flat dictionary lookup with no recursion, so the
  # inner `$color_scheme…` reaches the stylesheet unresolved and the browser drops the declaration
  # at parse. The ring is emitted and does not render. That is an xp85-side defect, not something
  # this screen can express around.
  states:
    hover:
      background: $color_scheme.light.color.surface-variant
      motion:
        transition: $motion.ease.out.150
        reduced: none

# --- Sidebar Navigation ---

xp87-sidebar:
  type: Nav
  semantic_role: navigation
  a11y:
    label: $t.nav.label
  spatial:
    display: flex
    width: $size.sidebar-width
    padding: $spacing.md
    gap: $spacing.xs
    overflow: auto
    responsive:
      sm:
        display: none
      md:
        display: none
  tokens:
    background: $color_scheme.light.color.surface
    border: $border.default
  children:
    - nav-today
    - nav-section-tracking
    - nav-section-asset
    - nav-section-materials
    - nav-section-config
    - nav-audit-trail
    - nav-section-platform
    - nav-section-admin
    - nav-docs
    - sidebar-footer-divider
    - xp87-sidebar-footer

# F71 §C2 — Logout moved off the sidebar so a misclick on Profile's neighbour
# never signs the user out. The Profile page (`/profile`) now hosts the
# Logout action; sidebar Profile row → land on profile → click Logout.

# Separator between the last nav section and the user/logout footer.
# Same theme-adaptive muted line as the flyout category divider
# (currentColor 18% mix) so it fades naturally in both light and dark
# schemes. The Divider emitter auto-hides this element when the sidebar
# is collapsed so the tight icon rail stays visually uninterrupted.
sidebar-footer-divider:
  type: Divider
  spatial:
    height: 0.0625rem
  tokens:
    background: "color-mix(in srgb, currentColor 18%, transparent)"

# Footer — F63 sidebar UX spec v2 D9: avatar above logout, stacked
# vertically, sticky to the bottom of the sidebar. The XP85 emitter
# auto-applies margin-block-start: auto to the nav's last child so the
# footer always pins to the bottom regardless of how many sections
# live above it.
xp87-sidebar-footer:
  type: Section
  permission: "public"
  spatial:
    display: flex
    direction: column
    align: stretch
    gap: $spacing.2xs
    # F71 §C2 — pad the footer away from the window bottom so the
    # logout row doesn't visually crowd the viewport edge in both
    # expanded and collapsed rail modes.
    padding_block_end: $spacing.md
  children:
    - sidebar-user-row

# Profile row: whole row is a clickable Section containing an Avatar icon and
# the signed-in user's display name. The Text auto-hides when the sidebar is collapsed
# (via the Text emitter's collapsed-host rule) and the Avatar auto-centers in
# the rail via margin-inline: auto. In expanded mode the icon sits at
# padding-start and the label at ~2.5rem — matching NavItem label alignment.
# A Section carries navigation via `link:`; the `action:` form (and its
# `return_context`) is not valid on a Structure node in the pinned emitter.
# No `a11y.label`: the link's accessible name is computed from its content, so
# it begins with the display name the row shows (WCAG 2.5.3 Label in Name). A
# host label would become an aria-label replacing that name, and the emitter
# sets it without placeholder interpolation. In the collapsed rail the labels
# are display:none and the avatar is decorative, so the name falls back to the
# `tooltip:` title, which carries the display name too.
sidebar-user-row:
  type: Section
  permission: "public"
  link: /profile
  # F71 §6 — native title= tooltip with "Logged in as ..." identity hint.
  # Resolves via locale interpolation against the i18n runtime; the user
  # context provides the {displayName} / {email} / {tenant} / {roles} placeholders.
  tooltip: $t.sidebar.profile_full
  spatial:
    display: flex
    align: center
    # F71 §C2 — `justify: center` aligns the Avatar within the row's
    # available width. In collapsed-rail mode the labels are hidden and
    # the Avatar is the only flex item, so center it. In expanded mode
    # the labels add content to the right and the row settles to
    # icon-start visually because the labels push the right side full.
    justify: center
    gap: $spacing.sm
    width: 100%
    min_height: $size.touch-target
    padding_inline: $spacing.sm
  tokens:
    color: $color_scheme.light.color.on-surface
    font_size: $font.size.sm
    font_weight: "600"
  states:
    hover:
      background: $color_scheme.light.color.surface-overlay
      motion:
        transition: $motion.ease.out.150
        reduced: none
  children:
    - sidebar-user
    - sidebar-user-label
    - sidebar-tenant-label

sidebar-user:
  type: Avatar
  # F71 §2 — bind src to userContext.photoUrl (OIDC `picture` / XP87
  # extension `photo_url`). When the value is null (pre-auth mount or
  # user without an uploaded photo) the emitter renders a two-letter
  # initials span computed from $user.displayName instead of the <img>,
  # so the browser never paints a broken-image icon.
  # Decorative: no `alt:`, so the emitter writes alt="" (the initials span is
  # aria-hidden). The row's name is the display name beside it; an alt here
  # would repeat it or put another word in front of it.
  src: $user.photoUrl
  fallback: initials
  spatial:
    width: 1.5rem
    height: 1.5rem

# The signed-in user's display name (router userContext, from the id_token:
# name → preferred_username → email), so the shell always says who is connected.
sidebar-user-label:
  type: Text
  content: $user.displayName

# F71 §3 — tenant name under the user name in expanded sidebar mode.
# Bound to tenantContext.name (resolved from JWT tenant_name claim, see
# xp85 RouterGenerator.cs). The emitter declares this._tenantCtx on every
# host whose subtree references $tenant.* (see IdentityBindingEmitter).
# Hidden in collapsed sidebar mode by the existing collapsed-host rule on
# sidebar-user-row children.
sidebar-tenant-label:
  type: Text
  content: $tenant.name
  tokens:
    font_size: $font.size.xs
    color: $color_scheme.light.color.on-surface-muted

# F79 — /today promoted to the first top-level sidebar item (direct click, no
# section wrapper) with the home icon. The OEE dashboard and the now-empty
# Dashboard section + header are removed. nav-today is "public" (every
# authenticated user has a today view).
nav-today:
  type: NavItem
  permission: "public"
  route: /today
  icon: $icon.house
  content: $t.nav.today
  a11y:
    label: $t.nav.today_label
  active_match: exact
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

# Tracking section
nav-section-tracking:
  type: Section
  permission_any: ["TrackingRecord:read", "TrackingRecord:sign", "TrackingForm:read", "FormAssignment:read", "Notification:read"]
  feature: tracking
  spatial:
    gap: $spacing.2xs
  children:
    - nav-label-tracking
    - nav-tracking-dashboard
    - nav-tracking-records
    - nav-tracking-reviews
    - nav-tracking-forms
    - nav-tracking-assignments
    - nav-notifications

nav-label-tracking:
  type: SectionHeader
  permission_any: ["TrackingRecord:read", "TrackingRecord:sign", "TrackingForm:read", "FormAssignment:read", "Notification:read"]
  feature: tracking
  icon: book-open
  content: $t.nav.section_tracking

nav-tracking-dashboard:
  type: NavItem
  permission: "TrackingRecord:read"
  feature: tracking
  route: /tracking
  icon: $icon.gauge
  content: $t.nav.tracking_dashboard
  a11y:
    label: $t.nav.tracking_dashboard_label
  active_match: exact
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

nav-tracking-records:
  type: NavItem
  permission: "TrackingRecord:read"
  feature: tracking
  route: /tracking/records
  icon: $icon.note-pencil
  content: $t.nav.tracking_records
  a11y:
    label: $t.nav.tracking_records_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

nav-tracking-reviews:
  type: NavItem
  permission: "TrackingRecord:sign"
  feature: tracking
  route: /tracking/reviews
  icon: $icon.check-circle
  content: $t.nav.tracking_reviews
  a11y:
    label: $t.nav.tracking_reviews_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md
  states:
    hover:
      background: $color_scheme.light.color.info-surface
      motion:
        transition: $motion.ease.out.150
        reduced: none

nav-tracking-forms:
  type: NavItem
  permission: "TrackingForm:read"
  feature: tracking
  route: /tracking/forms
  icon: $icon.clipboard-text
  content: $t.nav.tracking_forms
  a11y:
    label: $t.nav.tracking_forms_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

# F80 — form assignments (template ↔ asset-class bindings)
nav-tracking-assignments:
  type: NavItem
  permission: "FormAssignment:read"
  feature: tracking
  route: /tracking/assignments
  icon: $icon.check-circle
  content: $t.nav.form_assignments
  a11y:
    label: $t.nav.form_assignments_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

# Materials section
nav-section-materials:
  type: Section
  permission_any: ["ItemLot:read", "ItemLot:create", "ItemClass:read", "ItemDefinition:read"]
  feature: materials
  spatial:
    gap: $spacing.2xs
  children:
    - nav-label-materials
    - nav-lots
    - nav-receive
    - nav-classes
    - nav-definitions

nav-label-materials:
  type: SectionHeader
  permission_any: ["ItemLot:read", "ItemLot:create", "ItemClass:read", "ItemDefinition:read"]
  feature: materials
  icon: package
  content: $t.nav.section_materials

nav-lots:
  type: NavItem
  permission: "ItemLot:read"
  feature: materials
  route: /materials/lots
  icon: $icon.package
  content: $t.nav.lots
  a11y:
    label: $t.nav.lots_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

nav-receive:
  type: NavItem
  permission: "ItemLot:create"
  feature: materials
  route: /materials/lots/receive
  icon: $icon.download-simple
  content: $t.nav.receive
  a11y:
    label: $t.nav.receive_label
  active_match: exact
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

nav-classes:
  type: NavItem
  permission: "ItemClass:read"
  feature: materials
  route: /materials/classes
  icon: $icon.tag
  content: $t.nav.classes
  a11y:
    label: $t.nav.classes_label
  active_match: exact
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

nav-definitions:
  type: NavItem
  permission: "ItemDefinition:read"
  feature: materials
  route: /materials/definitions
  icon: $icon.list
  content: $t.nav.definitions
  a11y:
    label: $t.nav.definitions_label
  active_match: exact
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

# Configuration section — reason configuration: the vocabularies, and the
# transactions that offer their codes. Asset class/instance management is its
# own top-level Asset section (nav-section-asset); the generic process-platform
# config screens (process-binding, output-spec, process-def, document-def,
# statechart) and the /config/materials placeholder are removed from the M1 UI
# surface (routes + CIR pages deleted). permission_any keeps System:admin
# (admins always see the section) plus ReasonVocabulary:read — the one
# permission both items read under — so the section renders iff the user can
# see its items, never an empty "Configuration" ghost.
nav-section-config:
  type: Section
  permission_any: ["System:admin", "ReasonVocabulary:read"]
  spatial:
    gap: $spacing.2xs
  children:
    - nav-label-config
    - nav-config-reason-vocabularies
    - nav-config-reason-transactions

nav-label-config:
  type: SectionHeader
  permission_any: ["System:admin", "ReasonVocabulary:read"]
  icon: gear
  content: $t.nav.section_config

# Asset section — top-level (M1 UI surface reduction: asset class + instance
# management lifted out of Configuration into its own section, per the M1
# manual-test finding "asset needs its OWN folder"). The whole section gates on
# the `asset` feature, so a tenant with asset disabled sees no Asset nav at all
# (same pattern as nav-section-materials).
nav-section-asset:
  type: Section
  permission_any: ["AssetClass:write", "AssetInstance:read"]
  feature: asset
  spatial:
    gap: $spacing.2xs
  children:
    - nav-label-asset
    - nav-asset-classes
    - nav-asset-instances

nav-label-asset:
  type: SectionHeader
  permission_any: ["AssetClass:write", "AssetInstance:read"]
  feature: asset
  icon: factory
  content: $t.nav.section_asset

nav-asset-classes:
  type: NavItem
  permission: "AssetClass:write"
  feature: asset
  route: /asset/classes
  icon: $icon.factory
  content: $t.nav.config_asset
  a11y:
    label: $t.nav.config_asset_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md
  states:
    hover:
      background: $color_scheme.light.color.info-surface
      motion:
        transition: $motion.ease.out.150
        reduced: none

# F88 — Asset instance management (real Assets domain API). Gated on
# AssetInstance:read (distinct from the AssetClass:write-gated entry above).
nav-asset-instances:
  type: NavItem
  permission: "AssetInstance:read"
  feature: asset
  route: /asset/instances
  icon: $icon.package
  content: $t.nav.config_asset_instances
  a11y:
    label: $t.nav.config_asset_instances_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md
  states:
    hover:
      background: $color_scheme.light.color.info-surface
      motion:
        transition: $motion.ease.out.150
        reduced: none

# Reason vocabularies are tracking configuration, not tenant administration —
# the nav entry lives in the Config section (routes keep /admin/* paths).
nav-config-reason-vocabularies:
  type: NavItem
  permission: "ReasonVocabulary:read"
  route: /admin/reason-vocabularies
  icon: $icon.tag
  content: $t.nav.config_reason_vocabularies
  a11y:
    label: $t.nav.config_reason_vocabularies_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md
  states:
    hover:
      background: $color_scheme.light.color.info-surface
      motion:
        transition: $motion.ease.out.150
        reduced: none

# The per-transaction view of the same mapping (Feature #610): which actions
# ask for a reason, and the codes each offers.
nav-config-reason-transactions:
  type: NavItem
  permission: "ReasonVocabulary:read"
  route: /admin/reason-transactions
  icon: $icon.list
  content: $t.nav.config_reason_transactions
  a11y:
    label: $t.nav.config_reason_transactions_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md
  states:
    hover:
      background: $color_scheme.light.color.info-surface
      motion:
        transition: $motion.ease.out.150
        reduced: none

# Audit trail is a top-level sidebar item (direct click, no section
# wrapper) — same pattern as nav-today. A single-item section costs an
# extra expand click for no grouping benefit.
nav-audit-trail:
  type: NavItem
  permission: "AuditLog:read"
  route: /audit
  icon: $icon.shield-check
  content: $t.nav.audit_trail
  a11y:
    label: $t.nav.audit_trail_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

# Platform section — SystemAdmin only
nav-section-platform:
  type: Section
  permission: "System:admin"
  spatial:
    gap: $spacing.2xs
  children:
    - nav-label-platform
    - nav-platform-tenants
    - nav-admin-system

nav-label-platform:
  type: SectionHeader
  permission: "System:admin"
  # `factory` matches the section's tenant/plant scope. SectionHeaders use
  # bare icon names like every other nav-label-* node.
  icon: factory
  content: $t.nav.section_platform
  a11y:
    label: $t.nav.section_platform_label

nav-platform-tenants:
  type: NavItem
  permission: "System:admin"
  route: /admin/tenants
  icon: $icon.factory
  content: $t.nav.admin_tenants
  a11y:
    label: $t.nav.admin_tenants_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

# Admin section
nav-section-admin:
  type: Section
  permission_any: ["User:read", "Invitation:read", "Role:read", "TenantProfile:read", "UiCustomization:read"]
  spatial:
    gap: $spacing.2xs
  children:
    - nav-label-admin
    - nav-admin-my-tenant
    - nav-admin-customizations
    - nav-admin-users
    - nav-admin-invitations
    - nav-admin-roles

nav-label-admin:
  type: SectionHeader
  permission_any: ["User:read", "Invitation:read", "Role:read", "TenantProfile:read", "UiCustomization:read"]
  icon: users
  content: $t.nav.section_admin

# Feature #1630 — feature-gating edit 4 of 4. `feature: ui_customization` gates the
# nav node; the API route gate is the security boundary and this is UX, per
# feature-gating.md. It lands in the same commit as the page and the route, so
# all four layers are synchronized at the moment the surface exists — a diff
# carrying three of the four is a Medium-or-higher finding by that rule's terms.
nav-admin-customizations:
  type: NavItem
  permission: "UiCustomization:read"
  feature: ui_customization
  route: /customizations
  icon: $icon.file-text
  content: $t.nav.admin_customizations
  a11y:
    label: $t.nav.admin_customizations_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

# The one door to the org-profile edit screen: it edits the signed-in admin's own tenant.
nav-admin-my-tenant:
  type: NavItem
  permission: "TenantProfile:read"
  route: /admin/my-tenant/profile
  icon: $icon.factory
  content: $t.nav.admin_my_tenant
  a11y:
    label: $t.nav.admin_my_tenant_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

nav-admin-users:
  type: NavItem
  permission: "User:read"
  route: /admin/users
  icon: $icon.users
  content: $t.nav.admin_users
  a11y:
    label: $t.nav.admin_users_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

nav-admin-invitations:
  type: NavItem
  permission: "Invitation:read"
  route: /admin/invitations
  icon: $icon.arrow-right
  content: $t.nav.admin_invitations
  a11y:
    label: $t.nav.admin_invitations_label
  active_match: exact
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

nav-admin-roles:
  type: NavItem
  permission: "Role:read"
  route: /admin/roles
  icon: $icon.lock
  content: $t.nav.admin_roles
  a11y:
    label: $t.nav.admin_roles_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

nav-admin-system:
  type: NavItem
  permission: "System:admin"
  route: /admin/system
  icon: $icon.gear
  content: $t.nav.admin_system
  a11y:
    label: $t.nav.admin_system_label
  active_match: exact
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md

# F53b §F — nav-admin-audit removed; audit lifted to nav-audit-trail in /audit module.

# F71 §4 — Notifications nav entry with live unread-count badge. The badge
# resolves through notificationsContext, populated by the router's polling
# fetch of GET /api/notifications/unread/count (xp85 §4 G1); the chip is
# hidden via ?hidden while the resolved value is 0/null. F75 M1: this item
# lives in the Tracking section (notifications are tracking review/sign work in
# M1 scope) rather than its own section — mirrors how reason-vocabularies
# was folded into the Config section. The same unread_count drives the
# header-bell control.
nav-notifications:
  type: NavItem
  permission: "Notification:read"
  # Notifications is its own feature. It currently lives inside the Tracking
  # nav section, so it also inherits that section's `feature: tracking` gate —
  # disabling Tracking hides Notifications too. Lifting it to its own top-level
  # section (to decouple) is tracked as a follow-up.
  feature: notifications
  route: /notifications
  # Uses the `bell` glyph; keeps the item visible in collapsed-rail mode
  # alongside its unread badge.
  icon: $icon.bell
  content: $t.nav.notifications
  badge: $notifications.unread_count
  a11y:
    label: $t.nav.notifications_label
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md
  states:
    hover:
      background: $color_scheme.light.color.info-surface
      motion:
        transition: $motion.ease.out.150
        reduced: none

# Documentation is a top-level sidebar item (direct click, no section
# wrapper) — same pattern as nav-today. A single-item section costs an
# extra expand click for no grouping benefit.
nav-docs:
  type: NavItem
  permission: "public"
  route: /docs
  icon: $icon.book-open
  content: $t.docs.title
  active_match: prefix
  spatial:
    min_height: $size.touch-target
    padding: $spacing.sm
  tokens:
    radius: $radius.md
  states:
    hover:
      background: $color_scheme.light.color.info-surface
      motion:
        transition: $motion.ease.out.150
        reduced: none

# The layouts' default content slot — the one Slot node with no name (V07).
page-slot:
  type: Slot
